import MarkdownToJsx from 'markdown-to-jsx';
import React from 'react';

/**
 * AI narratives shouldn't contain headings, but if one slips through we render it
 * as bold body text rather than an oversized browser-default heading. Other rich
 * markdown (lists, emphasis, links) falls back to default element styling.
 */
const headingFallback = { component: 'strong' as const };

/**
 * Markdown image syntax (`![alt](url)`) is parsed even with raw HTML disabled and
 * would fetch a remote resource — a tracking/injection surface we don't want in
 * clinical narratives. Render the alt text only, never the image.
 */
const ImageFallback = ({ alt }: { alt?: string }): React.ReactElement => <>{alt}</>;

/**
 * markdown-to-jsx options for the AI narratives. It renders to React elements
 * (never `dangerouslySetInnerHTML`); the options below keep the output limited to
 * the safe, constrained subset we expect from LLM-generated copy.
 */
const markdownOptions: NonNullable<React.ComponentProps<typeof MarkdownToJsx>['options']> = {
  // Escape any literal HTML in the narrative instead of rendering it.
  disableParsingRawHTML: true,
  // Don't auto-link bare URLs in the text; narratives shouldn't contain links.
  disableAutoLink: true,
  overrides: {
    // Any explicit links open in a new tab; rel guards against tabnabbing + referrer leakage.
    a: { props: { target: '_blank', rel: 'noopener noreferrer' } },
    // Never fetch remote images — render the alt text instead (see ImageFallback).
    img: { component: ImageFallback },
    // Degrade stray headings to bold body text rather than oversized headings.
    h1: headingFallback,
    h2: headingFallback,
    h3: headingFallback,
    h4: headingFallback,
    h5: headingFallback,
    h6: headingFallback,
  },
};

type MarkdownProps = {
  children: string;
  /** Render inline (no block wrapper) — used for list items like recommendations. */
  inline?: boolean;
};

/**
 * Renders the AI narrative Markdown with our hardened options. Keeps the
 * markdown-to-jsx dependency and its configuration in one place so consumers
 * never touch the library directly.
 */
export const Markdown = ({ children, inline = false }: MarkdownProps): React.ReactElement => (
  <MarkdownToJsx options={inline ? { ...markdownOptions, forceInline: true } : markdownOptions}>
    {children}
  </MarkdownToJsx>
);
